import {makeAutoObservable} from "mobx";
import {getStore, useStore} from "./index.jsx";
import moment from "moment";
import {buildSearchQuery, makeQueryString, setToast} from "@/utils";

export class SearchBaseStore {

    /**
     * 리스트 선택시 특정 키값으로 관리
     * @type {Object}
     */
    checkedMap = {}

    /**
     * 섬색시에 사용 되는 세퇸 파라미터
     * @type {Object}
     */
    searchParams = {};

    constructor(store) {
        this.store = store;
        makeAutoObservable(this, {}, {autoBind: true});
    }

    /**
     * 현재 페이지에서 검색 정보를 URL에서 확인 하여 객체로 만들어 주는 함수
     * @param {object} params - 현재 페이지의 url 파라미터 객체
     * @param {object} init - 현재 페이지에서 사용할 검색 리스트 초기값
     * @return {string} 현재 페이지 검색 파라미터
     */
    setCurrentParams(params, init) {
        if (Object.keys(params).length > 0) {
            if(Object.keys(params)[0] === "menu-id" && Object.keys(params).length === 1){
                for (const key in init) {
                    if(typeof init[key] === "object"){
                        if (init[key].key) {
                            this.searchParams[init[key].key] = init[key].value ?? "";
                        }
                    } else {
                        this.searchParams[key] = init[key] ?? ""
                    }
                }
            }
            for (const key in params) {
                this.searchParams[key] = params[key];
            }
        } else {
            for (const key in init) {
                if(typeof init[key] === "object"){
                    if (init[key].key) {
                        this.searchParams[init[key].key] = init[key].value ?? "";
                    }
                } else {
                    this.searchParams[key] = init[key] ?? ""
                }
            }
        }
        const queryObject = buildSearchQuery(this.searchParams);
        return makeQueryString(queryObject);
    }
    /**
     * searchParams 객쳉에 지정한 키값과 내용 값을 바꾸는 함수
     * @param {string} key - 객체에 담을 키값
     * @param {string} value - 해당 키값의 객체에 담을 내용
     */
    setParamsChange(key, value) {
        this.searchParams[key] = value;
    }

    /**
     * 셀렉 박스 + 인풋 박스 검색시에만 사용 되는 함수
     * @param {string} typeKey - 객체에 담을 키값
     * @param {string} word - 해당 키값의 객체에 담을 내용
     */
    setSelectInput(typeKey, word) {
        if (!typeKey) return;
        this.searchParams[typeKey] = word ?? "";
    }

    /**
     * 셀렉 박스 + 인풋 박스 검색시에만 현재 셀렉트 박스의 value값을 지정 하는 함수
     * @param {[]} list - 셀렉트 박스 옵션 리스트
     * @return {string} SelectBox value 값
     */
    getSelectType(list) {
        const keys = Object.keys(this.searchParams);
        for (let i = 0; i < list.length; i++) {
            const value = list[i].value;
            if (keys.indexOf(value) !== -1) return value;
        }
        return "";
    }


    setDeleteOverlapExcept(keep, list) {
        const selectable = list.map(item => item.value);
        const keys = Object.keys(this.searchParams);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (selectable.indexOf(key) !== -1 && key !== keep) {
                delete this.searchParams[key];
            }
        }
    }

    setDeleteSearchParams(key){
        delete this.searchParams[key];
    }
    /**
     * 셀렉 박스 + 인풋 박스 검색시에만 현재 셀렉트 + 인풋 박스의 value값을 지정 하는 함수
     * @param {[]} list - 셀렉트 박스 옵션 리스트
     */
    getSelectWord(list) {
        const key = this.getSelectType(list);
        return key ? this.searchParams[key] : "";
    }


    /**
     * 특정 파라미터를 업데이트 해주는 함수
     * @param {string} key - 셀렉트 박스 옵션 리스트
     * @param {string} value - 셀렉트 박스 옵션 리스트
     */
    setUpdatedParams(key, value) {
        this.searchParams[key] = value;
    }

    /**
     * 파라미터 초기값으로 돌려 주는 초기화 함수
     * @param {object} params - 셀렉트 박스 옵션 리스트
     */
    setClearParams (params) {
        this.searchParams = params;
    }


    setSingleClearParams(key){
        delete this.searchParams[key]
    }




    /**
     * 검색영역에 체크 박스를 관리 하는 함수
     * @param {boolean} checked - 체크 박스 선택 유무
     * @param {string} value - 체크 박스의 리스트중 선택된 박스의 값
     * @param {[]} items - 세팅된 체크 박스의 리스트
     * @param {string} key - 선택된 체크 박스에 담길 파라미터 키값
     */
    setSearchCheckedSingle(checked, value, items = [], key) {
        const current = this.searchParams[`${key}`] || "";
        let selected = current === "all" ? [] : current.split(",").filter(item => item !== "");
        if (checked) {
            if (!selected.includes(value)) {
                selected.push(value);
            }
        } else {
            selected = selected.filter(v => v !== value);
        }
        const allValues = items.map(item => item.value);
        const isAllSelected = allValues.every(v => selected.includes(v));
        if (isAllSelected) {
            this.setParamsChange(key, "all");
        } else {
            this.setParamsChange(key, selected.join(","));
        }
    }


    /**
     * 달력 팝업에서 시작 날짜 선택하여 파라미터에 세팅 하는 함수
     * @param {string|date} date - 달력 팝업에서 선택된 날짜
     */
    setStartDate(date) {
        if (!date) return;
        const dateStr = moment(date).format("YYYY-MM-DD");
        const to = this.searchParams.end_date;

        this.searchParams.start_date = dateStr;

        if (to && moment(dateStr).isAfter(moment(to))) {
            this.searchParams.end_date = null;
        }
    }


    /**
     * 달력 팝업에서 끝나는 날짜 선택하여 파라미터에 세팅 하는 함수
     * @param {string|date} date - 달력 팝업에서 선택된 날짜
     */
    setEndDate(date) {
        if (!date) return;
        this.searchParams.end_date = moment(date).format("YYYY-MM-DD");
    }

    /**
     * 퀵 테이터 버튼 리스트중 선택 하여 날짜를 세팅하는 함수
     * @param {string|date} from - 퀵데이터 리스트중 시작 되는 날짜
     * @param {string|date} to - 퀵데이터 리스트중 끝나는 날짜
     */
    setTableDateRange(from, to) {
        this.searchParams.start_date = from;
        this.searchParams.end_date = to;
    }




    /**
     * 검색후 결과값으로 나온 테이블에서 데이터 체크리스중 전체를 체크하는 함수
     * @param {[]} items - 체크 맵에서 관리되는 체크한 리스트 내부엔 index값 주입
     * @param {boolean} checked - 체크 유무 확인
     * @param {string} key - 특정 키값으로 체크 맵만 관리 되는 유니크 인자값
     * @param {string} rowId - 특정 row id 값으로 체크 맵만 관리 되는 유니크 인자값
     */
    setAllChecked(items = [], checked = false, key, rowId = undefined) {
        if(rowId){
            const prevList = this.checkedMap[key] || [];
            const pageIds = items.map(item => item._id);
            if (checked) {
                const next = [...prevList];
                pageIds.forEach(id => {
                    if (!next.includes(id)) {
                        next.push(id);
                    }
                });
                this.checkedMap[key] = next;
            } else {
                this.checkedMap[key] = prevList.filter(id => !pageIds.includes(id));
            }
        }else {
            if (checked) {
                this.checkedMap[key] = items.map((_, index) => index);
            } else {
                this.checkedMap[key] = [];
            }
        }

    }

    /**
     * 검색후 결과값으로 나온 테이블에서 데이터 체크리스중 전체가 체크가 되었는지 판단 하는 함수
     * @param {[]} items - 체크 맵에서 관리되는 체크한 리스트 내부엔 index값 확인
     * @param {string} key - 특정 키값으로 체크 맵만 관리 되는 유니크 인자값
     * @return {boolean} 전체가 체크가 되었는지 확인 값
     * @param {string} rowId - 특정 row id 값으로 체크 맵만 관리 되는 유니크 인자값
     */
    getAllChecked(items = [], key, rowId = undefined) {
        if(rowId){
            const list = this.checkedMap[key] || [];
            if (!items || items.length === 0) return false;
            const notIncluded = items.filter(function (item) {
                const id = item._id;
                return list.indexOf(id) === -1;
            });
            return notIncluded.length === 0;
        }else {
            const allIds = items.map((_, index) => index);
            const selected = this.checkedMap[key] || [];
            return allIds.length > 0 && selected.length === allIds.length;
        }
    }


    /**
     * 검색후 결과값으로 나온 테이블에서 데이터 체크리스중 각각 체크 유무 확인 함수
     * @param {string|number} idx - 체크된 index값
     * @param {string} key - 특정 키값으로 체크 맵만 관리 되는 유니크 인자값
     * @return {boolean} 각각 리스트중 체크 되었는지 판단
     */
    getSingleChecked = (idx, key) => {
        return this.checkedMap[key]?.includes(idx) ?? false;
    };


    /**
     * 검색후 결과값으로 나온 테이블에서 데이터 특정 체크리스중 각각 체크하는 함수
     * @param {boolean} checked - 체크 박스 선택 유무
     * @param {string|number} id - 체크된 index값
     * @param {string} type - 특정 키값으로 체크 맵만 관리 되는 유니크 인자값
     */
    setSingleChecked(checked, id, type) {
        let list = this.checkedMap[type] || [];
        if (checked) {
            if (!list.includes(id)) {
                list = [...list, id];
            }
        } else {
            list = list.filter(item => item !== id);
        }
        this.checkedMap[type] = list;
    }


    /**
     * 현재 페이지 기준에서 특정 체크 맵을 초기화 시키는 함수
     * @param {string} key - 특정 키값으로 체크 맵만 관리 되는 유니크 인자값
     */
    setDeleteCheckedTypeMap(key) {
        delete this.checkedMap[key]
    }

    /**
     * 현재 페이지 기준에서 체크 맵을 전부 초기화 시키는 함수
     */
    setDeleteCheckedMap() {
        this.checkedMap = {};
    }

    clearBase() {
        this.checkedMap = {};
        this.searchParams = {};
    }



}

export const useSearchBaseStore = () => {
    return useStore().searchBaseStore;
}

export const getSearchBaseStore = () => {
    return getStore().searchBaseStore;
}
